import womanGuitarBg from "shared/assets/images/tour-guitar-bg.png";
import djBg from "shared/assets/images/tour-dj-bg.png";
import manGuitarBg from "shared/assets/images/tour-man-guitar-bg.png";
import "./TourVariantB.scss";
import { Progress } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ArtistState } from "explorer/reducers/artistas";
import { getGenrePercentageAtCurrentStage } from "shared/helpers/genre-artist-helpers";
import { fetchArtistsByGenre } from "shared/service/RadarService";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import bgLevelHighlight from "shared/assets/images/current-level-highlight.svg";
import { ReactComponent as Logo } from "shared/assets/images/logo-strm-horizontal.svg";
import Welcome from "./Welcome";
import { isMobile } from "react-device-detect";
import { range } from "lodash";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { motion } from "motion/react";
import { GenresState } from "explorer/reducers/generos";

const BackgroundLevelDetails = ({ show }: any) => {
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const { t } = useTranslation();
  const currentStageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentStageRef.current && show) {
      currentStageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [show]);

  return (
    <div className="artist-level">
      <div className="scroll-wrapper">
        {range(1, 11).map((stage) =>
          stage === userArtist.stage ? (
            <div
              className="level-item-current"
              key={`stage-A-${stage}`}
              ref={currentStageRef}
            >
              <div className="glow" />
              <div className="content">
                <p className="title">{t("pages.Tour.VariantB.youAt")}</p>
                <div
                  className="hexagon item-height-same"
                  style={{
                    background: `url(${bgLevelHighlight}) no-repeat center`,
                  }}
                >
                  <p className="level-text-current">
                    <Trans
                      i18nKey="pages.Tour.artistLevel"
                      components={{
                        span: <span className="level-number-current"> </span>,
                      }}
                      values={{
                        level: stage,
                      }}
                    />
                  </p>
                </div>
                <div className="current-level">
                  <p className="level-name">
                    <Trans
                      i18nKey={`pages.Tour.level.${stage}.artistInLine`}
                      components={{
                        span: (
                          <span className="color-light-lavender text-artist"></span>
                        ),
                      }}
                    />
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="level-item" key={`stage-B-${stage}`}>
              <span className="level-number">{stage}</span>
              <span
                className={`${
                  stage % 2 === 0 ? "level-name-right" : "level-name-left"
                }`}
              >
                {t(`pages.Tour.level.${stage}.name`)}
              </span>
            </div>
          )
        )}
        <div style={{ height: "100vh" }}>&nbsp;</div>
      </div>
    </div>
  );
};

const eventPageByStepName = {
  "welcome": tagManagerEvent.Tour.ONBOARDING_COPILOT,
  "stage-level": tagManagerEvent.Tour.ONBOARDING_STAGE_LEVEL,
  "goals": tagManagerEvent.Tour.ONBOARDING_GOALS,
  "quincy": tagManagerEvent.Tour.ONBOARDING_QUINCY,
};

const TourVariantB = () => {
  const { userArtist, loading: loadingUserArtist } = useSelector(
    (state: ArtistState) => state.artists
  );
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [artistsByGenre, setArtistsByGenre] = useState<any[]>([]);
  const { genre } = userArtist?.releaseFrequency || {};
  const { genres, loading: loadingGenres } = useSelector(
    (state: GenresState) => state.genres
  );
  const artistPercentageAtCurrentStage = getGenrePercentageAtCurrentStage(
    userArtist?.stage,
    artistsByGenre
  );
  const localeString = i18n.language === "en" ? "en-US" : "pt-BR";

  const steps = useMemo(
    () => [
      {
        name: "welcome",
        title: <Trans i18nKey="pages.Tour.VariantB.focusOnYourMusic" />,
        subtitle: <Trans i18nKey="pages.Tour.VariantB.focusOnYourMusic" />,
        background: () => (
          <div
            style={{
              background: `url(${womanGuitarBg}) no-repeat`,
              backgroundSize: "cover",
              width: "100%",
              height: "100%",
            }}
          />
        ),
      },
      {
        name: "stage-level",
        title: (
          <Trans
            i18nKey="pages.Tour.VariantB.congratulations"
            values={{
              porcentage:
                artistPercentageAtCurrentStage.toLocaleString(localeString),
              genre: getI18nGenre(genre, i18n, genres),
            }}
            shouldUnescape={true}
          />
        ),
        subtitle: <Trans i18nKey="pages.Tour.VariantB.seeYourCurrentLevel" />,
        background: (show: boolean) => <BackgroundLevelDetails show={show} />,
      },
      {
        name: "goals",
        title: <Trans i18nKey="pages.Tour.VariantB.yourPathIsClear" />,
        subtitle: <Trans i18nKey="pages.Tour.VariantB.personalizedGoals" />,
        background: () => (
          <div
            style={{
              background: `url(${djBg}) no-repeat`,
              backgroundSize: "cover",
              width: "100%",
              height: "100%",
            }}
          />
        ),
      },
      {
        name: "quincy",
        title: <Trans i18nKey="pages.Tour.VariantB.talkToQuincy" />,
        subtitle: <Trans i18nKey="pages.Tour.VariantB.youGotTheTalent" />,
        background: () => (
          <div
            style={{
              background: `url(${manGuitarBg}) no-repeat`,
              backgroundSize: "cover",
              width: "100%",
              height: "100%",
            }}
          />
        ),
      },
    ],
    [artistPercentageAtCurrentStage, localeString, genre, i18n, genres]
  );
  const eachStepPorcetage = 100 / steps.length;
  const finishedSteps = step >= steps.length;

  useEffect(() => {
    const getArtistByGenre = async () => {
      if (userArtist?.genresStrm?.length) {
        const response: any = await fetchArtistsByGenre(
          userArtist?.genresStrm[0]
        );
        setArtistsByGenre(response);
      }
    };
    getArtistByGenre();
  }, [userArtist?.genresStrm]);

  const dispatchedStepsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const currentStep = steps[step];
    if (
      currentStep?.name &&
      !dispatchedStepsRef.current.has(currentStep.name)
    ) {
      dataLayerViewedEvent({
        ...eventPageByStepName[
          currentStep.name as keyof typeof eventPageByStepName
        ],
        ABTest: "FLOW_B",
      });
      dispatchedStepsRef.current.add(currentStep.name);
    }
  }, [step, steps]);

  if (loadingGenres || loadingUserArtist) return <div className="tour-b"></div>;

  return (
    <div className="tour-b">
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: finishedSteps ? 0 : 1 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        className={`logo ${isMobile && "mobile"}`}
      >
        <p>
          <Logo />
          <span
            style={{
              marginTop: 8,
              marginLeft: 12,
              color: "#F2F2F2",
              fontSize: 12,
            }}
          >
            for artists
          </span>
        </p>
      </motion.div>
      <div className={`container ${!isMobile && "desktop"}`}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="motion-steps"
        >
          {steps.map((x, index) => (
            <Fragment key={`step-${index}`}>
              <div
                className="wrapper-background"
                style={{
                  opacity: index === step ? 1 : 0,
                  display: finishedSteps ? "none" : undefined,
                }}
              >
                {x.background(index === step)}
              </div>

              <div
                className="step"
                style={{
                  display: index === step ? "flex" : "none",
                }}
              >
                <div className="progress-info">
                  {!isMobile && (
                    <Progress
                      percent={(step + 1) * eachStepPorcetage}
                      steps={steps.length}
                      showInfo={false}
                      rootClassName="progress-bars"
                    />
                  )}
                </div>
                <div className="card-info">
                  {isMobile && (
                    <Progress
                      percent={(step + 1) * eachStepPorcetage}
                      steps={steps.length}
                      showInfo={false}
                      rootClassName="progress-bars into-card"
                    />
                  )}
                  <p className="title">{x.title}</p>
                  <p className="subtitle">{x.subtitle}</p>
                  <button
                    className="botao-azul btn-forward"
                    onClick={() => {
                      setStep((prev) => prev + 1);
                    }}
                  >
                    {t("pages.Tour.next")}
                  </button>
                </div>
              </div>
            </Fragment>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: finishedSteps ? 1 : 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          hidden={!finishedSteps}
          style={{
            position: "absolute",
          }}
          className={`wrapper-background`}
        >
          <Welcome show={finishedSteps} />
        </motion.div>
      </div>
    </div>
  );
};

export default TourVariantB;
